/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useCallback, type JSX } from 'react';

import {
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  LexicalEditor
} from 'lexical';


import { ImageAsset } from '@cms/components/data/Asset';
import { SystemDialogContent } from '@cms/components/data/Dialog';
import { AssetsPageContent } from '@cms/pages/app/AssetsPage';
import { useDialog } from '@cms/providers/DialogProvider';
import { ISystemAsset } from '@dl/declarations';
import {
  $createImageNode,
  ImagePayload
} from '../../nodes/ImageNode';


export type InsertImagePayload = Readonly<ImagePayload>;

export function InsertImageDialog({
  activeEditor,
}: {
  activeEditor: LexicalEditor;
}): JSX.Element {
  const { closeDialog } = useDialog();

  const handleUseImage = (asset: ISystemAsset) => {
    activeEditor.update(() => {
      const payload: InsertImagePayload = {
        src: asset.src_url || '',
        alt_text: asset.headline || '',
        headline: asset.headline || '',
        credit: asset.credit || '',
        copyright: asset.copyright || '',
        article_caption: asset.article_caption || '',
      }

      const selection = $getSelection();

      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const node = $createImageNode(payload);
        $insertNodes([
          node,
          // $createParagraphNode()
        ]);
        // $applyNodeReplacement(node);
      }

      closeDialog();
    })
  }

  const renderItem = useCallback((row: ISystemAsset) => {
    switch (row.mime_type) {
      case 'image':
        return (
          <ImageAsset
            asset={row}
            actions={{
              useImage: {
                show: true,
                action(asset) {
                  handleUseImage(asset);
                },
              },
              editImage: {
                show: true,
              },
            }}
          />
        )

      case 'video':
        return null

      default:
        return null
    }
  }, [])

  return (
    <SystemDialogContent
      content={{
        component: (
          <AssetsPageContent
            renderItem={renderItem}
          />
        )
      }}
      dialogProps={{
        title: 'Add asset to article'
      }}
      actionsProps={{
        hide: true,
        cancel: {
          onClick: closeDialog
        }
      }}
    />
  )
}
